import { action, makeObservable, observable, runInAction } from "mobx";
import {
  PurhcaseRequestFilters,
  PurhcaseRequestDetails,
} from "../models/PurchaseRequest/PurchaseRequestDetails";
import { PurchaseRequestRejectedBy } from "../models/PurchaseRequest/PurchaseRequestBody";
import PurchaseRequestAPI from "../api/PurchaseRequestAPI";
import { UserStore } from "./UserStore";
import OrderAPI from "../api/OrderAPI";
import { APIBlobResponse } from "../models/NetworkResponse";
import { PurchaseRequestProduct } from "../models/PurchaseRequest/PurchaseRequestProductInsights";
import { VendorsPOReportAPIFilters } from "../models/Vendors/VendorsPOReportFilters";
import { VendorsPOReportData } from "../models/Vendors/VendorsPOReportData";

export class PurchaseRequestStore {
  purchaseRequestAPI: PurchaseRequestAPI;
  prlist: PurhcaseRequestDetails[];
  limit: number;
  total_pages: number;
  total_count: number;
  purchaseRequestDetail: PurhcaseRequestDetails | null;
  prProductAvailableQty: { [key: number]: number };
  editDialog: {
    open: boolean;
    productId: number | null;
    field: string | null;
  };
  cancellationModal: {
    open: boolean;
    poId: number | null;
    reason: string;
  };
  statusChangeModal: {
    open: boolean;
    poId: number | null;
    status: string | null;
  };
  sendingEmails: Set<number>;
  updatingStatus: Set<number>;
  orderAPI: OrderAPI;
  prFilter: string;
  vendorsPOReportData: VendorsPOReportData[];

  constructor() {
    this.purchaseRequestAPI = new PurchaseRequestAPI();
    this.prlist = [];
    this.limit = 0;
    this.total_count = 0;
    this.total_pages = 0;
    this.purchaseRequestDetail = null;
    this.prProductAvailableQty = {};
    this.vendorsPOReportData = [];
    this.editDialog = {
      open: false,
      productId: null,
      field: null,
    };
    this.cancellationModal = {
      open: false,
      poId: null,
      reason: "",
    };
    this.statusChangeModal = {
      open: false,
      poId: null,
      status: null,
    };
    this.sendingEmails = new Set();
    this.updatingStatus = new Set();
    this.orderAPI = new OrderAPI();
    this.prFilter = "PENDING";

    makeObservable(this, {
      prlist: observable,
      limit: observable,
      total_count: observable,
      total_pages: observable,
      getPurchaseRequestListing: action,
      approveRequest: action,
      rejectRequest: action,
      purchaseRequestDetail: observable,
      getPurchaseRequestDetail: action,
      prProductAvailableQty: observable,
      getPrProductAvailableQty: action,
      editDialog: observable,
      openEditDialog: action,
      closeEditDialog: action,
      updateProductField: action,
      downloadPurchaseOrder: action,
      cancellationModal: observable,
      statusChangeModal: observable,
      sendingEmails: observable,
      updatingStatus: observable,
      openCancellationModal: action,
      closeCancellationModal: action,
      setCancellationReason: action,
      openStatusChangeModal: action,
      closeStatusChangeModal: action,
      addSendingEmail: action,
      removeSendingEmail: action,
      addUpdatingStatus: action,
      removeUpdatingStatus: action,
      sendEmailToVendor: action,
      updatePOStatus: action,
      generatePO: action,
      reactivatePO: action,
      cancelPurchaseRequest: action,
      prFilter: observable,
      setPrFilter: action,
      vendorsPOReportData: observable,
      getVendorsPOReportData: action,
    });
  }

  getPurchaseRequestListing = async (filters: PurhcaseRequestFilters) => {
    try {
      const response =
        await this.purchaseRequestAPI.getPurchaseRequestListing(filters);
      this.limit = response.limit;
      this.total_count = response.total_count;
      this.total_pages = response.total_pages;
      this.prlist = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  approveRequest = async (
    prId: number,
    team: PurchaseRequestRejectedBy,
    userStore: UserStore,
  ) => {
    try {
      const response = await this.purchaseRequestAPI.approvePurchaseRequest(
        prId,
        team,
      );
      if (response.success) {
        if (team === PurchaseRequestRejectedBy.FINANCE) {
          this.prlist = this.prlist.map((pr) =>
            pr.id === prId
              ? {
                ...pr,
                finance_approval: 1,
                finance_approval_date: new Date().toISOString(),
                approved_by_finance_admin_id: userStore.user?.id || null,
                approved_by_finance_adminname: userStore.user?.name || null,
              }
              : pr,
          );
        } else if (team === PurchaseRequestRejectedBy.SOURCING) {
          this.prlist = this.prlist.map((pr) =>
            pr.id === prId
              ? {
                ...pr,
                sourcing_approval: 1,
                sourcing_approval_date: new Date().toISOString(),
                approved_by_sourcing_admin_id: userStore.user?.id || null,
                approved_by_sourcing_adminname: userStore.user?.name || null,
              }
              : pr,
          );
        } else if (team === PurchaseRequestRejectedBy.SWAGEAZY) {
          this.prlist = this.prlist.map((pr) =>
            pr.id === prId
              ? {
                ...pr,
                swageazy_approval: 1,
                swageazy_approval_date: new Date().toISOString(),
                approved_by_swageazy_admin_id: userStore.user?.id || null,
                approved_by_swageazy_adminname: userStore.user?.name || null,
              }
              : pr,
          );
        }
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  rejectRequest = async (
    prId: number,
    team: PurchaseRequestRejectedBy,
    userStore: UserStore,
    rejectionReason?: string,
  ) => {
    try {
      const response = await this.purchaseRequestAPI.rejectPurchaseRequest(
        prId,
        team,
        rejectionReason,
      );
      if (response.success) {
        if (team === PurchaseRequestRejectedBy.FINANCE) {
          this.prlist = this.prlist.map((pr) =>
            pr.id === prId
              ? {
                ...pr,
                finance_approval: 0,
                rejected_by_finance_admin_id: userStore.user?.id || null,
                rejected_by_finance_adminname: userStore.user?.name || null,
                rejection_date_by_finance: new Date().toISOString(),
              }
              : pr,
          );
        } else if (team === PurchaseRequestRejectedBy.SOURCING) {
          this.prlist = this.prlist.map((pr) =>
            pr.id === prId
              ? {
                ...pr,
                sourcing_approval: 0,
                rejected_by_sourcing_admin_id: userStore.user?.id || null,
                rejected_by_sourcing_adminname: userStore.user?.name || null,
                rejection_date_by_sourcing: new Date().toISOString(),
              }
              : pr,
          );
        } else if (team === PurchaseRequestRejectedBy.SWAGEAZY) {
          this.prlist = this.prlist.map((pr) =>
            pr.id === prId
              ? {
                ...pr,
                swageazy_approval: 0,
                rejected_by_swageazy_admin_id: userStore.user?.id || null,
                rejected_by_swageazy_adminname: userStore.user?.name || null,
                rejection_date_by_swageazy: new Date().toISOString(),
              }
              : pr,
          );
        }
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  generatePO = async (prId: number) => {
    try {
      const response = await this.purchaseRequestAPI.generatePO(prId);
      if (response.success) {
        this.prlist = this.prlist.map((pr) =>
          pr.id === prId
            ? { ...pr, po_generated: 1, po_id: response.result, active_po: 1 }
            : pr,
        );
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getPurchaseRequestDetail = async (prId: number) => {
    try {
      const response =
        await this.purchaseRequestAPI.getPurchaseRequestDetail(prId);
      this.purchaseRequestDetail = response;
    } catch (error: any) {
      throw error;
    }
  };

  openEditDialog = (productId: number, field: string) => {
    this.editDialog = {
      open: true,
      productId,
      field,
    };
  };

  closeEditDialog = () => {
    this.editDialog = {
      open: false,
      productId: null,
      field: null,
    };
  };

  openCancellationModal = (poId: number) => {
    this.cancellationModal = {
      open: true,
      poId,
      reason: "",
    };
  };

  closeCancellationModal = () => {
    this.cancellationModal = {
      open: false,
      poId: null,
      reason: "",
    };
  };

  setCancellationReason = (reason: string) => {
    this.cancellationModal.reason = reason;
  };

  openStatusChangeModal = (poId: number, status: string) => {
    this.statusChangeModal = {
      open: true,
      poId,
      status,
    };
  };

  closeStatusChangeModal = () => {
    this.statusChangeModal = {
      open: false,
      poId: null,
      status: null,
    };
  };

  addSendingEmail = (poId: number) => {
    this.sendingEmails.add(poId);
  };

  removeSendingEmail = (poId: number) => {
    this.sendingEmails.delete(poId);
  };

  addUpdatingStatus = (poId: number) => {
    this.updatingStatus.add(poId);
  };

  removeUpdatingStatus = (poId: number) => {
    this.updatingStatus.delete(poId);
  };

  updateProductField = async (
    product: PurchaseRequestProduct,
    value: string | number,
    field: string,
    projectId?: number,
  ) => {
    try {
      if (!product || !field || !this.purchaseRequestDetail || !projectId) {
        throw new Error("Missing required parameters");
      }

      let response;
      let updatedValue = value;

      // Handle different field types
      if (field === "rate") {
        const to_update_product = {
          ...product,
          rate: Number(value),
        };

        response =
          await this.purchaseRequestAPI.updatePurchaseRequestProduct(
            to_update_product,
          );
        updatedValue = Number(value);
      } else if (field === "gst_percentage") {
        const to_update_product = {
          ...product,
          gst_percentage: Number(value),
        };

        response =
          await this.purchaseRequestAPI.updatePurchaseRequestProduct(
            to_update_product,
          );
        updatedValue = Number(value);
      } else if (field === "hsn_code") {
        // Update product HSN code
        response = await this.orderAPI.updateProductHsn(
          projectId,
          product.product_id,
          Number(value),
          product.pr_id,
        );
        updatedValue = String(value);
      } else if (field === "quantity") {
        // Update purchase request product quantity
        const to_update_product = {
          ...product,
          quantity: Number(value),
        };

        response =
          await this.purchaseRequestAPI.updatePurchaseRequestProduct(
            to_update_product,
          );
        updatedValue = Number(value);
      } else {
        throw new Error(`Unsupported field: ${field}`);
      }

      // Only update local state if API call was successful
      if (response && (response as any).success !== false) {
        const updatedProducts = this.purchaseRequestDetail.products?.map(
          (pr) =>
            pr.product_id === product.product_id
              ? { ...pr, [field]: updatedValue }
              : pr,
        );

        this.purchaseRequestDetail = {
          ...this.purchaseRequestDetail,
          products: updatedProducts,
        };
      } else {
        throw new Error("API call failed");
      }

      this.closeEditDialog();
      return response;
    } catch (error) {
      this.closeEditDialog();
      throw error;
    }
  };

  getPrProductAvailableQty = async (productIds: number[], orderId: number) => {
    try {
      const response = await this.purchaseRequestAPI.getPrProductAvailableQty(
        productIds,
        orderId,
      );
      this.prProductAvailableQty = response.reduce(
        (
          acc: { [key: number]: string },
          item: { id: number; value: string },
        ) => {
          acc[item.id] = item.value;
          return acc;
        },
        {},
      );
    } catch (error: any) {
      throw error;
    }
  };

  downloadPurchaseOrder = async (po_id: number) => {
    try {
      const response =
        await this.purchaseRequestAPI.downloadPurchaseOrder(po_id);
      return response as APIBlobResponse;
    } catch (error: any) {
      throw error;
    }
  };

  sendEmailToVendor = async (pr_id: number, po_id: number) => {
    this.addSendingEmail(po_id);
    try {
      const response = await this.purchaseRequestAPI.sendEmailToVendor(
        pr_id,
        po_id,
      );
      if (response.success && this.purchaseRequestDetail) {
        this.purchaseRequestDetail.purchase_orders =
          this.purchaseRequestDetail.purchase_orders?.map((po) =>
            po.id === po_id
              ? {
                ...po,
                email_sent: true,
                email_sent_date: new Date().toISOString(),
              }
              : po,
          ) || [];
      }
      return response;
    } catch (error: any) {
      throw error;
    } finally {
      this.removeSendingEmail(po_id);
    }
  };

  updatePOStatus = async (
    po_id: number,
    status: string,
    cancellationReason?: string,
  ) => {
    this.addUpdatingStatus(po_id);
    try {
      const response = await this.purchaseRequestAPI.updatePOStatus(
        po_id,
        status,
        cancellationReason,
      );
      if (response.success && this.purchaseRequestDetail) {
        const prId = this.purchaseRequestDetail.id;
        const updatedOrders =
          this.purchaseRequestDetail.purchase_orders?.map((po) => {
            if (po.id !== po_id) return po;
            if (status === "CANCELLED") {
              return {
                ...po,
                status,
                cancellation_reason: cancellationReason,
              };
            }
            if (status === "CREATED" || status === "OPEN") {
              return {
                ...po,
                status,
                cancellation_reason: undefined,
                cancelled_by_admin_id: undefined,
                cancelled_by_adminname: undefined,
              };
            }
            return { ...po, status };
          }) || [];

        const hasActivePo = updatedOrders.some(
          (po) => po.status === "CREATED" || po.status === "OPEN",
        );

        this.purchaseRequestDetail = {
          ...this.purchaseRequestDetail,
          purchase_orders: updatedOrders,
          active_po: hasActivePo ? 1 : 0,
          po_id: this.purchaseRequestDetail.po_id || po_id,
        };

        this.prlist = this.prlist.map((pr) =>
          pr.id === prId ? { ...pr, active_po: hasActivePo ? 1 : 0 } : pr,
        );
      }
      return response;
    } catch (error: any) {
      throw error;
    } finally {
      this.removeUpdatingStatus(po_id);
    }
  };

  cancelPurchaseRequest = async (
    pr_id: number,
    userStore: UserStore,
    cancellationReason?: string,
  ) => {
    try {
      const response = await this.purchaseRequestAPI.cancelPurchaseRequest(
        pr_id,
        cancellationReason,
      );
      if (response.success) {
        this.prlist = this.prlist.map((pr) =>
          pr.id === pr_id
            ? {
              ...pr,
              pr_cancellation_reason: cancellationReason || null,
              active: 0,
              pr_cancellation_date: new Date().toISOString(),
              pr_cancelled_by_admin_id: userStore.user?.id || null,
              pr_cancelled_by_admin_name: userStore.user?.name || null,
            }
            : pr,
        );
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  setPrFilter = (filter: string) => {
    runInAction(() => {
      this.prFilter = filter;
    });
  };

  getVendorsPOReportData = async (filters: VendorsPOReportAPIFilters) => {
    try {
      const response =
        await this.purchaseRequestAPI.getVendorsPOReportData(filters);
      this.vendorsPOReportData = response.result;
    } catch (error: any) {
      throw error;
    }
  };

  reactivatePO = async (prId: number, poId: number) => {
    try {
      const response = await this.purchaseRequestAPI.reactivatePO(prId, poId);
      if (response.success) {
        if (this.purchaseRequestDetail?.id === prId) {
          this.purchaseRequestDetail = {
            ...this.purchaseRequestDetail,
            po_id: poId,
            active_po: 1,
            purchase_orders:
              this.purchaseRequestDetail.purchase_orders?.map((po) =>
                po.id === poId
                  ? {
                    ...po,
                    status: "CREATED",
                    cancellation_reason: undefined,
                    cancelled_by_admin_id: undefined,
                    cancelled_by_adminname: undefined,
                  }
                  : po,
              ) || [],
          };
        }
        // used for listing page
        this.prlist = this.prlist.map((pr) =>
          pr.id === prId ? { ...pr, active_po: 1, po_id: poId } : pr,
        );
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

}
